import React, { useState, useEffect } from 'react';
import './ManageAdditionalInfo.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSave,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationCircle,
  FaPaperclip,
  FaClipboardList,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';

const FIELD_TYPE_LABELS = {
  text: 'Short Text',
  textarea: 'Long Text',
  number: 'Number',
  date: 'Date'
};

const emptyForm = {
  title: '',
  description: '',
  fieldType: 'text',
  required: false,
  allowUpload: false,
  courses: []
};

const ManageAdditionalInfo = () => {
  const token = localStorage.getItem('token');

  const [fields, setFields] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedField, setSelectedField] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchFields();
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchFields = async () => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/additional-info/fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setFields(data.fields || []);
      } else {
        setError(data.message || 'Failed to fetch fields');
      }
    } catch (err) {
      console.error('Error fetching additional info fields:', err);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setCourses(data.courses || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const openModal = (mode, field = null) => {
    setModalMode(mode);
    setSelectedField(field);
    setError('');

    if (mode === 'create') {
      setFormData(emptyForm);
    } else if (field) {
      setFormData({
        title: field.title,
        description: field.description || '',
        fieldType: field.fieldType,
        required: field.required,
        allowUpload: field.allowUpload,
        courses: (field.courses || []).map(c => c.courseId?._id || c.courseId)
      });
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedField(null);
    setFormData(emptyForm);
    setError('');
  };

  const toggleCourse = (courseId) => {
    setFormData(prev => {
      const has = prev.courses.includes(courseId);
      return {
        ...prev,
        courses: has ? prev.courses.filter(id => id !== courseId) : [...prev.courses, courseId]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.courses.length === 0) {
      setError('Please select at least one department');
      return;
    }

    try {
      const url = modalMode === 'create'
        ? `${collegeConfig.api.baseURL}/additional-info/fields`
        : `${collegeConfig.api.baseURL}/additional-info/fields/${selectedField._id}`;
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const payload = {
        ...formData,
        courses: formData.courses.map(id => {
          const course = courses.find(c => c._id === id);
          return { courseId: id, courseName: course?.name || '' };
        })
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message);
        fetchFields();
        closeModal();
      } else {
        setError(data.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Error saving field:', err);
      setError('Error connecting to server');
    }
  };

  const handleToggleActive = async (field) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/additional-info/fields/${field._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !field.isActive })
      });
      if (res.ok) {
        fetchFields();
      }
    } catch (err) {
      console.error('Error toggling field:', err);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Delete this field? All student responses for it will also be removed.')) {
      return;
    }
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/additional-info/fields/${fieldId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSuccess('Field deleted successfully');
        fetchFields();
      } else {
        setError('Failed to delete field');
      }
    } catch (err) {
      console.error('Error deleting field:', err);
      setError('Error connecting to server');
    }
  };

  if (loading) {
    return (
      <div className="mai-loading">
        <div className="spinner"></div>
        <p>Loading additional information fields...</p>
      </div>
    );
  }

  return (
    <div className="mai-container">
      <div className="mai-header">
        <div className="header-content">
          <h2><FaClipboardList style={{ marginRight: 10 }} />Manage Additional Information</h2>
          <p>Create custom fields for students to fill in (e.g. ICR Letter Reference Number)</p>
        </div>
        <button className="btn-create-mai" onClick={() => openModal('create')}>
          <FaPlus /> Add New Field
        </button>
      </div>

      {success && <div className="alert alert-success"><FaCheckCircle /> {success}</div>}
      {error && !showModal && <div className="alert alert-error"><FaExclamationCircle /> {error}</div>}

      <div className="mai-list">
        {fields.length === 0 ? (
          <div className="mai-empty">
            <FaInfoCircle className="mai-empty-icon" />
            <h3>No fields created yet</h3>
            <p>Add a field to start collecting additional information from students.</p>
          </div>
        ) : (
          fields.map(field => (
            <div key={field._id} className={`mai-card ${!field.isActive ? 'inactive' : ''}`}>
              <div className="mai-card-main">
                <div className="mai-card-info">
                  <h3>{field.title}</h3>
                  {field.description && <p className="mai-desc">{field.description}</p>}
                  <div className="mai-meta">
                    <span className="mai-badge type">{FIELD_TYPE_LABELS[field.fieldType]}</span>
                    {field.required && <span className="mai-badge required">Required</span>}
                    {field.allowUpload && <span className="mai-badge upload"><FaPaperclip /> Upload allowed</span>}
                    {!field.isActive && <span className="mai-badge disabled">Disabled</span>}
                  </div>
                  <div className="mai-departments">
                    {(field.courses || []).map((c, idx) => (
                      <span key={idx} className="mai-dept-chip">{c.courseName}</span>
                    ))}
                  </div>
                </div>
                <div className="mai-card-actions">
                  <button
                    className="btn-icon btn-toggle"
                    onClick={() => handleToggleActive(field)}
                    title={field.isActive ? 'Disable field' : 'Enable field'}
                  >
                    {field.isActive ? <FaToggleOn /> : <FaToggleOff />}
                  </button>
                  <button className="btn-icon btn-edit" onClick={() => openModal('edit', field)} title="Edit">
                    <FaEdit />
                  </button>
                  <button className="btn-icon btn-delete" onClick={() => handleDelete(field._id)} title="Delete">
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'create' ? 'Add New Field' : 'Edit Field'}</h3>
              <button className="modal-close" onClick={closeModal}><FaTimes /></button>
            </div>

            {error && <div className="alert alert-error"><FaExclamationCircle /> {error}</div>}

            <form onSubmit={handleSubmit} className="mai-form">
              <div className="form-group">
                <label>Field Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. ICR Letter Reference Number"
                  required
                />
              </div>

              <div className="form-group">
                <label>Instructions (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Any guidance for students filling this field"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Field Type *</label>
                  <select
                    value={formData.fieldType}
                    onChange={(e) => setFormData({ ...formData, fieldType: e.target.value })}
                  >
                    {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.required}
                      onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                    />
                    <span className="checkbox-text">Mandatory for students</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.allowUpload}
                      onChange={(e) => setFormData({ ...formData, allowUpload: e.target.checked })}
                    />
                    <span className="checkbox-text"><FaPaperclip /> Allow file upload alongside answer</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Departments * (Select who should see this field)</label>
                <div className="mai-course-grid">
                  {courses.map(course => (
                    <label key={course._id} className="checkbox-label mai-course-option">
                      <input
                        type="checkbox"
                        checked={formData.courses.includes(course._id)}
                        onChange={() => toggleCourse(course._id)}
                      />
                      <span className="checkbox-text">{course.name} ({course.code})</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FaSave /> {modalMode === 'create' ? 'Create Field' : 'Update Field'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAdditionalInfo;
